import { dapiFetchCache } from "@origin-digital/daxi";
import { logger } from "@origin-digital/reporting-client";

//4 Minutes
export const TOKEN_SHIELD_POLL_INTERVAL = 240000;
const POLL_TIME_KEY = "@od/tokenPollTime";
const POLL_RESULT_KEY = "@od/tokenPollValid";

const storage = (): Storage => window.localStorage;

export const storePollTime = (pollTime: Date, valid?: boolean) => {
  storage().setItem(POLL_TIME_KEY, pollTime.getTime().toString());
  storage().setItem(POLL_RESULT_KEY, valid?.toString() ?? "");
};

const getPollTimeFromStorage = () => {
  const storedPollString = storage().getItem(POLL_TIME_KEY);
  const storedPollResultString = storage().getItem(POLL_RESULT_KEY);
  const storedResult = storedPollResultString
    ? storedPollResultString !== "false"
    : undefined;

  if (storedPollString === null) {
    return { date: undefined, valid: undefined };
  }
  const storedPollTime = parseInt(storedPollString, 10);
  if (isNaN(storedPollTime)) {
    return { date: undefined, valid: undefined };
  }
  return { date: new Date(storedPollTime), valid: storedResult };
};

export const fetchTokenShieldValid = async (): Promise<boolean | undefined> => {
  try {
    const res = await dapiFetchCache<{ valid: boolean }>(
      "/api/tokenshield/v1/validate",
      {
        ttlInMin: 1,
        allowStaleData: false,
        authorization: "jwt",
      }
    );
    const valid = res.data?.valid;
    storePollTime(new Date(), valid);
    return valid;
  } catch (error) {
    logger.info("error fetching tokenshield", { error });
    // If unable to get response, token is unvalidated, return undefined. Token shield should take no action
    return undefined;
  }
};

/* Compare pollTime to stored last poll, update and return newest poll
 * Can be used to prevent repeated polling across multiple tabs
 * */
export const multicastPollTime = (lastPollTime?: Date) => {
  const { date, valid } = getPollTimeFromStorage();
  if (!lastPollTime) {
    return { lastPollTime: date, valid };
  }
  if (!date || lastPollTime > date) {
    storePollTime(lastPollTime, undefined);
    return { lastPollTime, valid: undefined };
  }
  return { lastPollTime: date, valid };
};

export const pollIntervalExceeded = (lastPollTime: Date | undefined) => {
  const { lastPollTime: mergedPollTime, valid } =
    multicastPollTime(lastPollTime);
  if (!mergedPollTime) {
    return { shouldPoll: true };
  }
  const shouldPoll =
    Date.now() - mergedPollTime.getTime() > TOKEN_SHIELD_POLL_INTERVAL;
  if (!shouldPoll) {
    return { shouldPoll, valid };
  }
  return { shouldPoll };
};
